'use client'

import { RecipeVariantProps, sva } from '@/styled-system/css'
import { type CSSProperties } from 'react'
import useGameSettings from '../shared/settings/useGameSettings'

const tileRecipe = sva({
  slots: ['root', 'cover', 'content'],
  base: {
    root: {
      position: 'relative',
      fontSize: '1.5em',
      borderRadius: '0.5em',
      transitionProperty: 'background, box-shadow',
      transitionDuration: 'var(--duration)',
      cursor: 'pointer',
      _after: {
        content: '""',
        display: 'block',
        pb: '100%',
      },
      _disabled: {
        cursor: 'not-allowed',
        pointerEvents: 'none',
      },
    },
    cover: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      borderRadius: '0.5em',
      transitionProperty: 'transform, background, box-shadow',
      transitionDuration: 'var(--duration)',
      bg: 'neutral.300',
      boxShadow: '0 0.3em token(colors.neutral.200)',
    },
    content: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      bgSize: '70%',
      bgPosition: 'center',
      bgRepeat: 'no-repeat',
      animationDuration: 'var(--duration)',
      animationFillMode: 'forwards',
      animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
      animationName: 'backgroundScale',
    },
  },
  variants: {
    status: {
      idle: {
        cover: {
          animationName: 'scale-in',
          animationDuration: 'var(--duration)',
          animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
          _hover: {
            bg: 'neutral.400',
            boxShadow: '0 0.3em token(colors.neutral.300)',
          },
          _active: {
            bg: 'neutral.400',
            boxShadow: '0 0.15em token(colors.neutral.300)',
            transform: 'translateY(0.15em)',
          },
        },
      },
      fetching: {
        cover: {
          animationName: 'tile-pulse',
          animationDuration: 'var(--fetch-duration)',
          animationDelay: 'var(--duration)',
          animationIterationCount: 'infinite',
        },
      },
      gem: {
        root: {
          bg: 'neutral.50',
        },
        cover: {
          animationName: 'scale-out',
          animationDuration: 'var(--duration)',
          animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
          animationFillMode: 'forwards',
        },
      },
      mine: {
        root: {
          bg: 'neutral.50',
        },
        cover: {
          animationName: 'scale-out',
          animationDuration: 'var(--duration)',
          animationTimingFunction: 'cubic-bezier(0.87, -0.41, 0.19, 1.44)',
          animationFillMode: 'forwards',
        },
      },
    },
    selected: {
      true: {
        cover: {
          bg: 'pink',
          boxShadow: '0 0.3em token(colors.pink.950)',
        },
      },
    },
    revealed: {
      true: {},
    },
  },
  compoundVariants: [
    {
      revealed: false,
      status: ['gem', 'mine'],
      css: {
        content: {
          opacity: 0.3,
          transform: 'scale(0.7)',
        },
      },
    },
  ],
})

export type MinesTileProps = RecipeVariantProps<typeof tileRecipe> & {
  disabled?: boolean
  onClick?: () => void
}

import { memo, useMemo } from 'react'

const MinesTile = memo(function MinesTile(props: MinesTileProps) {
  const [variantProps, tileProps] = tileRecipe.splitVariantProps(props)
  const { ...rootProps } = tileProps
  const styles = tileRecipe(variantProps)
  const animationsEnabled = useGameSettings((state) => state.animationsEnabled)

  // Memoize URLs to avoid recreating them on each render
  const gemUrl = 'url(https://imagedelivery.net/9WutbSXJqeY8Kc44mJMbvA/gem.webp/width=200)'
  const mineUrl = 'url(https://imagedelivery.net/9WutbSXJqeY8Kc44mJMbvA/mine.webp/width=200)'

  // Only render content when needed (lazy loading)
  const shouldRenderContent = variantProps.status === 'gem' || variantProps.status === 'mine'

  // Use useMemo for the styles to prevent recalculation on every render
  const buttonStyle = useMemo(() => ({
    '--mine': mineUrl,
    '--gem': gemUrl,
    '--duration': animationsEnabled ? '0.3s' : '0s',
    '--fetch-duration': animationsEnabled ? '0.6s' : '0s',
  } as CSSProperties), [animationsEnabled, gemUrl, mineUrl])

  const contentStyle = useMemo(() => ({
    backgroundImage: variantProps.status === 'gem' ? gemUrl : mineUrl,
  }), [variantProps.status, gemUrl, mineUrl])

  return (
    <button
      className={styles.root}
      style={buttonStyle}
      {...rootProps}
    >
      {shouldRenderContent && (
        <div
          className={styles.content}
          style={contentStyle}
        />
      )}

      <div className={styles.cover} />
    </button>
  )
})

export default MinesTile
